import { makeStyles, ProgressBar } from '@fluentui/react-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../api/features/features';
import { CalculationExecutionStatusCode } from '../../model';
import { CommandType } from '../../utils/EventGrid.constants';

export interface IReportListPanelStatusBar {
    commandType: CommandType | undefined;
    statusCode: CalculationExecutionStatusCode | undefined;
}

const useStyles = makeStyles({
    description: {
        fontSize: '10px',
    },
});

export const ReportListPanelStatusBar: React.FC<IReportListPanelStatusBar> = (props: IReportListPanelStatusBar) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { data: isExportPrintFormsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'ExportPrintFormsEnabled',
    });

    const getDescription = () => {
        switch (props.commandType) {
            case CommandType.Print:
                if (isExportPrintFormsEnabled) {
                    switch (props.statusCode) {
                        case CalculationExecutionStatusCode.Executing:
                            return t('preparingPrintDownload').toString();
                        case CalculationExecutionStatusCode.Completed:
                        case CalculationExecutionStatusCode.CompletedWithErrors:
                            return t('readyPrintDownload').toString();
                        case CalculationExecutionStatusCode.Terminated:
                        case CalculationExecutionStatusCode.TimedOut:
                            return t('printDownloadFailed').toString();
                        default:
                            return undefined;
                    }
                } else {
                    return undefined;
                }

            default:
                switch (props.statusCode) {
                    case CalculationExecutionStatusCode.Executing:
                        return t('calculating').toString();
                    case CalculationExecutionStatusCode.Completed:
                    case CalculationExecutionStatusCode.CompletedWithErrors:
                        return t('complete').toString();
                    case CalculationExecutionStatusCode.Terminated:
                    case CalculationExecutionStatusCode.TimedOut:
                    case CalculationExecutionStatusCode.Canceled:
                        return t('calculationFailed').toString();
                    default:
                        return undefined;
                }
        }
    };

    const getBarColor = (): 'brand' | 'success' | 'error' => {
        switch (props.statusCode) {
            case CalculationExecutionStatusCode.Completed:
            case CalculationExecutionStatusCode.CompletedWithErrors:
                return 'success';
            case CalculationExecutionStatusCode.Terminated:
            case CalculationExecutionStatusCode.TimedOut:
            case CalculationExecutionStatusCode.Canceled:
                return 'error';
            default:
                return 'brand';
        }
    };

    const isExecuting = props.statusCode === CalculationExecutionStatusCode.Executing;
    const description = getDescription();

    if (!description) {
        return null;
    }

    return (
        <div>
            <ProgressBar shape='square' color={getBarColor()} value={isExecuting ? undefined : 1} aria-label={description} />
            <div className={styles.description}>{description}</div>
        </div>
    );
};

export default ReportListPanelStatusBar;
